/**
 * Company → Web (ADR-024 §4.10, rev 4): the Company's portals — its own
 * sign-in front door on a domain Fias manages for it (`staff.devv.com`). The
 * domains themselves are CompanyWebDomains.tsx.
 *
 * Each portal is its own front door, with a name ("Employee portal"), an
 * access policy (members of the Company's workspaces only — the default — or
 * open to any Fias account, which needs an explicit confirmation) and its own
 * Portal users list (S14: username and display name; email only for people
 * who consented under the disclosure that names it — rev 5, R20).
 *
 * The path an owner walks, one portal at a time:
 *   1. add a domain (buy one, move one, or delegate a subdomain);
 *   2. add a portal on it → Fias writes its DNS, issues a certificate and
 *      goes live (minutes) — nothing for the owner to publish;
 *   3. remove it → it stops at once, and Fias removes its records.
 *
 * Owners edit; admins see. Rendered only when the Company holds `company_web`.
 * The server enforces every rule here; this is the affordance.
 */

import { useState, type FormEvent } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExternalLink } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/use-toast';
import { api, ApiError, type CompanyWebDto, type CompanyWebHostDto } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';
import { ArcheSearchCombobox } from './ArcheSearchCombobox';
import { CompanyWebDomains } from './CompanyWebDomains';

const SELECT_CLASS =
  'h-9 rounded-md border border-input bg-background px-3 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring';

type Policy = CompanyWebHostDto['accessPolicy'];
const POLICY_LABEL: Record<Policy, string> = {
  members_only: 'Members of your workspaces',
  open: 'Anyone with a Fias account',
};

function errorText(err: unknown): string {
  if (err instanceof ApiError) return err.message.replace(/^[A-Z_]+:\s*/, '');
  return 'Something went wrong. Please try again.';
}

/** One line saying where a portal is and what, if anything, the owner must do. */
export function describeHost(host: CompanyWebHostDto): {
  badge: string;
  tone: 'ok' | 'wait' | 'bad';
  detail: string | null;
} {
  if (host.holds.includes('security')) {
    return {
      badge: 'Stopped by Fias',
      tone: 'bad',
      detail: 'Fias stopped this portal for a security check. Contact Fias support.',
    };
  }
  if (host.holds.includes('admin')) {
    return { badge: 'Paused by Fias', tone: 'bad', detail: 'Contact Fias support.' };
  }
  if (host.holds.includes('billing')) {
    return { badge: 'Paused', tone: 'bad', detail: 'Paused for billing.' };
  }
  switch (host.status) {
    case 'pending_dns':
      return {
        badge: 'Setting up',
        tone: 'wait',
        detail: 'Fias sets this portal up once its domain is live — usually a few minutes.',
      };
    case 'provisioning':
      return {
        badge: 'Going live',
        tone: 'wait',
        detail: 'Fias is issuing a certificate — usually a few minutes.',
      };
    case 'active':
      return { badge: 'Live', tone: 'ok', detail: null };
    case 'failed':
      return {
        badge: 'Needs attention',
        tone: 'bad',
        detail: host.certFailureReason
          ? `The certificate could not be issued (${host.certFailureReason}). Fias retries automatically.`
          : 'Fias could not finish setting this portal up. Contact Fias support if it persists.',
      };
    case 'removing':
      return { badge: 'Removing', tone: 'wait', detail: 'This portal is off and being removed.' };
    default:
      return { badge: host.status, tone: 'wait', detail: null };
  }
}

export function CompanyWebCard({ companyId }: { companyId: string }) {
  const qc = useQueryClient();
  const web = useQuery({
    queryKey: queryKeys.companies.web(companyId),
    queryFn: () => api.getCompanyWeb(companyId),
    // Domains and portals move on their own (registration → DNS → certificate
    // → live): follow them while anything is moving BY ITSELF. States that
    // wait on the owner (`pending_review`) or on days-long outside processes
    // (`pending_transfer`, `pending_delegation`) are checked once a minute,
    // not every 15 s — each poll fans out to a lookup per portal and domain.
    refetchInterval: (q) => {
      const data = q.state.data;
      if (!data) return false;
      const moving =
        data.hosts.some((h) => ['pending_dns', 'provisioning', 'removing'].includes(h.status)) ||
        data.domains.some((d) => d.status === 'pending_registration');
      if (moving) return 15_000;
      // A registered domain stays `removing` until it transfers out or expires.
      const waiting = data.domains.some((d) =>
        ['pending_review', 'pending_transfer', 'pending_delegation', 'removing'].includes(d.status),
      );
      return waiting ? 60_000 : false;
    },
  });
  const refresh = () => void qc.invalidateQueries({ queryKey: queryKeys.companies.web(companyId) });

  if (web.isError) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Portals</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-2">
          <p role="alert" className="text-sm text-destructive">
            Could not load your portals. {errorText(web.error)}
          </p>
          <Button size="sm" variant="outline" onClick={() => void web.refetch()}>
            Try again
          </Button>
        </CardContent>
      </Card>
    );
  }
  if (web.isLoading) {
    return (
      <p role="status" className="text-sm text-muted-foreground">
        Loading portals…
      </p>
    );
  }
  if (!web.data?.webEnabled) return null;
  const data = web.data;
  // Only an owner changes portals (the server enforces it; this is the UI).
  const isOwner = data.role === 'owner';

  return (
    <div className="space-y-6">
      <p className="text-sm text-muted-foreground">
        Give your people your own address for Fias — like <em>staff.yourcompany.com</em> — with your
        branding. They sign in with their Fias account on fias.io and continue to your portal, or on
        the portal itself with a code emailed to them; nobody types a password on your domain.
      </p>
      {!isOwner && (
        <p role="status" className="text-sm text-muted-foreground">
          Only Company owners can add or change domains and portals.
        </p>
      )}
      <section aria-labelledby="web-domains" className="space-y-3">
        <h3 id="web-domains" className="text-base font-semibold">
          Domains
        </h3>
        <CompanyWebDomains
          companyId={companyId}
          data={data}
          isOwner={isOwner}
          onChanged={refresh}
        />
      </section>
      <section aria-labelledby="web-portals" className="space-y-3">
        <h3 id="web-portals" className="text-base font-semibold">
          Portals
        </h3>
        {data.hosts.map((host) => (
          <HostCard
            key={host.hostId}
            companyId={companyId}
            host={host}
            isOwner={isOwner}
            onChanged={refresh}
          />
        ))}
        {isOwner && <AddPortal companyId={companyId} data={data} onAdded={refresh} />}
      </section>
    </div>
  );
}

function AddPortal({
  companyId,
  data,
  onAdded,
}: {
  companyId: string;
  data: CompanyWebDto;
  onAdded: () => void;
}) {
  // A portal can be added on any domain that is not on its way out; it goes
  // live once its domain does.
  const usable = data.domains.filter((d) => !['failed', 'removing', 'removed'].includes(d.status));
  const [sub, setSub] = useState('');
  // The choice, if it is still a usable domain; otherwise the first one. Read
  // on every render, so a domain that arrives after mounting (the first one,
  // added moments ago) is selectable without a reload.
  const [chosenDomainId, setDomainId] = useState('');
  const domainId = usable.some((d) => d.domainId === chosenDomainId)
    ? chosenDomainId
    : (usable[0]?.domainId ?? '');
  const zone = usable.find((d) => d.domainId === domainId)?.zoneName ?? '';
  const hostname = sub.trim() ? `${sub.trim().toLowerCase()}.${zone}` : zone;
  const [label, setLabel] = useState('');
  const [policy, setPolicy] = useState<Policy>('members_only');
  const [openConfirmed, setOpenConfirmed] = useState(false);
  const [landingId, setLandingId] = useState<string | null>(null);
  const [landingQuery, setLandingQuery] = useState('');
  const full = data.hosts.length >= data.maxHosts;
  const add = useMutation({
    mutationFn: () =>
      api.claimCompanyWebHost(companyId, {
        domainId,
        hostname,
        displayLabel: label.trim(),
        accessPolicy: policy,
        ...(landingId ? { landingArcheId: landingId } : {}),
      }),
    onSuccess: () => {
      setSub('');
      setLabel('');
      setPolicy('members_only');
      setOpenConfirmed(false);
      setLandingId(null);
      setLandingQuery('');
      onAdded();
    },
    onError: (err) =>
      toast({
        title: 'Could not add the portal',
        description: errorText(err),
        variant: 'destructive',
      }),
  });
  const canAdd =
    !full &&
    label.trim().length > 0 &&
    zone.length > 0 &&
    (policy === 'members_only' || openConfirmed) &&
    !add.isPending;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (canAdd) add.mutate();
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Add a portal</CardTitle>
        <CardDescription>
          {full
            ? `You have ${data.maxHosts} portals, the most a Company can have.`
            : usable.length === 0
              ? data.maxDomains === 0
                ? 'Your web access does not include a domain yet — ask Fias to allow one.'
                : 'Add a domain above first.'
              : 'Choose its address and who may use it. Fias sets up its DNS and certificate.'}
        </CardDescription>
      </CardHeader>
      {!full && usable.length > 0 && (
        <CardContent>
          <form className="space-y-4" onSubmit={submit}>
            <div className="grid gap-1.5">
              <Label htmlFor="portal-sub">Address</Label>
              <div className="flex flex-wrap items-center gap-2">
                <Input
                  id="portal-sub"
                  className="w-40"
                  placeholder="staff"
                  value={sub}
                  maxLength={63}
                  onChange={(e) => setSub(e.target.value)}
                />
                <span className="text-sm text-muted-foreground">.</span>
                <select
                  aria-label="Domain"
                  className={SELECT_CLASS}
                  value={domainId}
                  onChange={(e) => setDomainId(e.target.value)}
                >
                  {usable.map((d) => (
                    <option key={d.domainId} value={d.domainId}>
                      {d.zoneName}
                    </option>
                  ))}
                </select>
              </div>
              <p className="text-xs text-muted-foreground">
                Leave the first part empty to use {zone || 'the domain'} itself.
              </p>
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="portal-label">Name</Label>
              <Input
                id="portal-label"
                placeholder="Employee portal"
                value={label}
                maxLength={80}
                onChange={(e) => setLabel(e.target.value)}
              />
            </div>
            <PolicyPicker
              id="portal-policy"
              value={policy}
              onChange={(p) => {
                setPolicy(p);
                setOpenConfirmed(false);
              }}
              openConfirmed={openConfirmed}
              onOpenConfirmed={setOpenConfirmed}
            />
            <LandingPicker
              id="portal-landing"
              companyId={companyId}
              policy={policy}
              query={landingQuery}
              onQueryChange={setLandingQuery}
              landingId={landingId}
              onLandingChange={setLandingId}
            />
            <Button type="submit" disabled={!canAdd}>
              {add.isPending ? 'Adding…' : 'Add portal'}
            </Button>
          </form>
        </CardContent>
      )}
    </Card>
  );
}

/**
 * The arche a portal opens once someone is signed in (optional). The Company's
 * own arches — owned or granted, usually restricted to its people and so never
 * in the store — are offered first, on focus; a typed name searches the store
 * too. The server still requires a published, active arche.
 */
function LandingPicker({
  id,
  companyId,
  policy,
  query,
  onQueryChange,
  landingId,
  onLandingChange,
}: {
  id: string;
  companyId: string;
  policy: Policy;
  query: string;
  onQueryChange: (q: string) => void;
  landingId: string | null;
  onLandingChange: (id: string | null) => void;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>Landing page (optional)</Label>
      <div className="flex gap-2">
        <div className="flex-1">
          <ArcheSearchCombobox
            id={id}
            scopeKey={`web-landing-${companyId}`}
            search={(q) => api.searchCompanyApprovalArches(companyId, q)}
            value={query}
            onValueChange={(v) => {
              onQueryChange(v);
              if (v.trim() === '') onLandingChange(null);
            }}
            onSelect={(arche) => onLandingChange(arche.archeId)}
            placeholder="Your Company’s arches, or search published arches"
            companyScope={{}}
          />
        </div>
        {landingId && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              onLandingChange(null);
              onQueryChange('');
            }}
          >
            Clear
          </Button>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        The arche people land on once they&apos;re signed in. Leave empty for the usual Fias home.
        {policy === 'open' && ' If the arche is public, visitors see it before signing in too.'}
      </p>
    </div>
  );
}

function PolicyPicker({
  id,
  value,
  onChange,
  openConfirmed,
  onOpenConfirmed,
}: {
  id: string;
  value: Policy;
  onChange: (p: Policy) => void;
  openConfirmed: boolean;
  onOpenConfirmed: (v: boolean) => void;
}) {
  return (
    <div className="grid gap-1.5">
      <Label htmlFor={id}>Who can use it</Label>
      <select
        id={id}
        className={SELECT_CLASS}
        value={value}
        onChange={(e) => onChange(e.target.value as Policy)}
      >
        {(Object.keys(POLICY_LABEL) as Policy[]).map((p) => (
          <option key={p} value={p}>
            {POLICY_LABEL[p]}
          </option>
        ))}
      </select>
      {value === 'open' && (
        <label className="flex items-start gap-2 text-sm">
          <Checkbox
            checked={openConfirmed}
            onCheckedChange={(v) => onOpenConfirmed(v === true)}
            aria-label="Confirm an open portal"
          />
          <span>
            I understand anyone with a Fias account can sign in here. They see only their own data
            and anything already shared with them — never your workspaces.
          </span>
        </label>
      )}
    </div>
  );
}

function HostCard({
  companyId,
  host,
  isOwner,
  onChanged,
}: {
  companyId: string;
  host: CompanyWebHostDto;
  isOwner: boolean;
  onChanged: () => void;
}) {
  const state = describeHost(host);
  const [editing, setEditing] = useState(false);
  const [removing, setRemoving] = useState(false);
  const [showUsers, setShowUsers] = useState(false);
  const removed = host.status === 'removing';

  const remove = useMutation({
    mutationFn: () => api.removeCompanyWebHost(companyId, host.hostId),
    onSuccess: () => {
      setRemoving(false);
      onChanged();
    },
    onError: (err) =>
      toast({ title: 'Could not remove', description: errorText(err), variant: 'destructive' }),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex flex-wrap items-center gap-2">
          {host.displayLabel}
          <Badge
            variant={
              state.tone === 'bad' ? 'destructive' : state.tone === 'ok' ? 'default' : 'secondary'
            }
          >
            {state.badge}
          </Badge>
        </CardTitle>
        <CardDescription className="flex flex-wrap items-center gap-2">
          {host.status === 'active' ? (
            <a
              href={`https://${host.hostname}/`}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 underline"
            >
              {host.hostname} <ExternalLink className="h-3 w-3" />
            </a>
          ) : (
            <span>{host.hostname}</span>
          )}
          <span>· {POLICY_LABEL[host.accessPolicy]}</span>
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {state.detail && <p className="text-sm">{state.detail}</p>}
        {editing ? (
          <EditHost
            companyId={companyId}
            host={host}
            onDone={() => {
              setEditing(false);
              onChanged();
            }}
            onCancel={() => setEditing(false)}
          />
        ) : (
          <div className="flex flex-wrap gap-2">
            {isOwner && !removed && (
              <Button size="sm" variant="outline" onClick={() => setEditing(true)}>
                Edit
              </Button>
            )}
            {!removed && (
              <Button size="sm" variant="outline" onClick={() => setShowUsers((v) => !v)}>
                {showUsers ? 'Hide portal users' : 'Portal users'}
              </Button>
            )}
            {isOwner && !removed && (
              <Button size="sm" variant="ghost" onClick={() => setRemoving(true)}>
                Remove
              </Button>
            )}
          </div>
        )}
        {showUsers && <PortalUsers companyId={companyId} host={host} />}
      </CardContent>

      <AlertDialog open={removing} onOpenChange={setRemoving}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove {host.hostname}?</AlertDialogTitle>
            <AlertDialogDescription>
              The portal stops at once and everyone signed in there is signed out. Their Fias
              accounts and data are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction disabled={remove.isPending} onClick={() => remove.mutate()}>
              Remove portal
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
}

function EditHost({
  companyId,
  host,
  onDone,
  onCancel,
}: {
  companyId: string;
  host: CompanyWebHostDto;
  onDone: () => void;
  onCancel: () => void;
}) {
  const [label, setLabel] = useState(host.displayLabel);
  const [policy, setPolicy] = useState<Policy>(host.accessPolicy);
  const [openConfirmed, setOpenConfirmed] = useState(host.accessPolicy === 'open');
  const [landingId, setLandingId] = useState<string | null>(host.landingArcheId);
  // Start from the arche's name, never its raw id.
  const [landingQuery, setLandingQuery] = useState(host.landingArcheName ?? '');
  const save = useMutation({
    mutationFn: () =>
      api.updateCompanyWebHost(companyId, host.hostId, {
        ...(label.trim() !== host.displayLabel ? { displayLabel: label.trim() } : {}),
        ...(policy !== host.accessPolicy ? { accessPolicy: policy } : {}),
        ...(landingId !== host.landingArcheId ? { landingArcheId: landingId } : {}),
      }),
    onSuccess: onDone,
    onError: (err) =>
      toast({ title: 'Could not save', description: errorText(err), variant: 'destructive' }),
  });
  const changed =
    label.trim() !== host.displayLabel ||
    policy !== host.accessPolicy ||
    landingId !== host.landingArcheId;
  const canSave =
    changed &&
    label.trim().length > 0 &&
    (policy === 'members_only' || openConfirmed) &&
    !save.isPending;

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        if (canSave) save.mutate();
      }}
    >
      <div className="grid gap-1.5">
        <Label htmlFor={`label-${host.hostId}`}>Name</Label>
        <Input
          id={`label-${host.hostId}`}
          value={label}
          maxLength={80}
          onChange={(e) => setLabel(e.target.value)}
        />
      </div>
      <PolicyPicker
        id={`policy-${host.hostId}`}
        value={policy}
        onChange={(p) => {
          setPolicy(p);
          setOpenConfirmed(p === host.accessPolicy && p === 'open');
        }}
        openConfirmed={openConfirmed}
        onOpenConfirmed={setOpenConfirmed}
      />
      <LandingPicker
        id={`landing-${host.hostId}`}
        companyId={companyId}
        policy={policy}
        query={landingQuery}
        onQueryChange={setLandingQuery}
        landingId={landingId}
        onLandingChange={setLandingId}
      />
      {host.accessPolicy === 'open' && policy === 'members_only' && (
        <p className="text-sm text-muted-foreground">
          People who are not members of your workspaces will be signed out of this portal.
        </p>
      )}
      <div className="flex gap-2">
        <Button type="submit" size="sm" disabled={!canSave}>
          Save
        </Button>
        <Button type="button" size="sm" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
}

const PORTAL_USERS_PAGE = 50;

function PortalUsers({ companyId, host }: { companyId: string; host: CompanyWebHostDto }) {
  const qc = useQueryClient();
  const [confirmBlock, setConfirmBlock] = useState<{ userId: string; name: string } | null>(null);
  const users = useInfiniteQuery({
    queryKey: queryKeys.companies.webUsers(companyId, host.hostId),
    queryFn: ({ pageParam }) =>
      api.listCompanyWebPortalUsers(companyId, host.hostId, {
        limit: PORTAL_USERS_PAGE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    // A full page means there may be more; a short one is the end.
    getNextPageParam: (last, pages) =>
      last.users.length === PORTAL_USERS_PAGE ? pages.length * PORTAL_USERS_PAGE : undefined,
  });
  const block = useMutation({
    mutationFn: (v: { userId: string; blocked: boolean }) =>
      api.setCompanyWebPortalUserBlocked(companyId, host.hostId, v.userId, v.blocked),
    onSuccess: () =>
      void qc.invalidateQueries({ queryKey: queryKeys.companies.webUsers(companyId, host.hostId) }),
    onError: (err) =>
      toast({ title: 'Could not save', description: errorText(err), variant: 'destructive' }),
  });

  if (users.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (users.isError) {
    return (
      <p role="alert" className="text-sm text-destructive">
        Could not load portal users. {errorText(users.error)}
      </p>
    );
  }
  const list = users.data?.pages.flatMap((p) => p.users) ?? [];
  return (
    <div className="space-y-2">
      <p className="text-xs text-muted-foreground">
        People who chose to continue to this portal. They agreed to share their username and display
        name with you, and their email address where it is shown (people who agreed before portals
        shared email are asked again the next time they continue).
        {host.accessPolicy === 'members_only' &&
          ' Access here follows workspace membership: remove someone from your workspaces to remove their access.'}
      </p>
      {list.length === 0 ? (
        <p className="text-sm text-muted-foreground">Nobody has used this portal yet.</p>
      ) : (
        <ul className="divide-y rounded-md border">
          {list.map((u) => {
            const name = u.displayName ?? (u.username ? `@${u.username}` : 'this person');
            return (
              <li key={u.userId} className="flex flex-wrap items-center justify-between gap-2 p-2">
                <div className="min-w-0 text-sm">
                  <span className="font-medium">{u.displayName ?? 'Unnamed'}</span>
                  {u.username && <span className="text-muted-foreground"> @{u.username}</span>}
                  {u.email && <div className="break-all text-xs">{u.email}</div>}
                  <div className="text-xs text-muted-foreground">
                    {u.joinedVia === 'registered_here'
                      ? 'Joined Fias here'
                      : 'Existing Fias account'}{' '}
                    · last used {new Date(u.lastSeenAt).toLocaleDateString()}
                    {u.blocked && ' · blocked'}
                  </div>
                </div>
                {/* Blocking is for open portals; lifting a block is always
                    possible — a block left from an open period still applies,
                    and would otherwise be stuck. */}
                {(host.accessPolicy === 'open' || u.blocked) && (
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={block.isPending}
                    aria-label={u.blocked ? `Unblock ${name}` : `Block ${name}`}
                    onClick={() =>
                      u.blocked
                        ? block.mutate({ userId: u.userId, blocked: false })
                        : setConfirmBlock({ userId: u.userId, name })
                    }
                  >
                    {u.blocked ? 'Unblock' : 'Block'}
                  </Button>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {users.hasNextPage && (
        <Button
          size="sm"
          variant="outline"
          disabled={users.isFetchingNextPage}
          onClick={() => void users.fetchNextPage()}
        >
          {users.isFetchingNextPage ? 'Loading…' : 'Show more'}
        </Button>
      )}
      <AlertDialog open={confirmBlock !== null} onOpenChange={(o) => !o && setConfirmBlock(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Block {confirmBlock?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              They are signed out of this portal and cannot continue to it until you unblock them.
              Their Fias account is not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                if (confirmBlock) block.mutate({ userId: confirmBlock.userId, blocked: true });
                setConfirmBlock(null);
              }}
            >
              Block
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
